/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: cdrSettingsUtils.ts
 🗓️ Created: 2025-01-27
 🧩 Description:
    Utility functions for CDR Settings module.
────────────────────────────────────────────────────────────────*/

import { DbConnectionSettings, CdrSettingsData } from '../types';

export const DEFAULT_DB_INFO_STRING =
  'host=127.0.0.1 port=5432 dbname=r2a_cdr user=r2a_user password=r2a2025 connect_timeout=10';

/**
 * Normalize token value
 */
export function normalizeTokenValue(value: string): string {
  return value.replace(/^['"]|['"]$/g, '').trim();
}

/**
 * Parse database info string
 */
export function parseDbInfoString(raw: string | null | undefined): DbConnectionSettings {
  const details: DbConnectionSettings = {
    host: '',
    port: '',
    database: '',
    user: '',
    password: '',
    connectTimeout: '',
    options: '',
  };

  if (!raw) {
    return details;
  }

  const tokens = raw
    .split(/\s+/)
    .map((token) => token.trim())
    .filter(Boolean);

  const extras: string[] = [];

  tokens.forEach((token) => {
    const [key, ...valueParts] = token.split('=');
    if (!key || valueParts.length === 0) {
      extras.push(token);
      return;
    }

    const normalizedKey = key.trim().toLowerCase();
    const value = normalizeTokenValue(valueParts.join('='));

    switch (normalizedKey) {
      case 'host':
        details.host = value;
        break;
      case 'port':
        details.port = value;
        break;
      case 'dbname':
      case 'database':
        details.database = value;
        break;
      case 'user':
      case 'username':
        details.user = value;
        break;
      case 'password':
        details.password = value;
        break;
      case 'connect_timeout':
      case 'connecttimeout':
        details.connectTimeout = value;
        break;
      default:
        extras.push(`${normalizedKey}=${value}`);
        break;
    }
  });

  if (extras.length) {
    details.options = extras.join(' ');
  }

  return details;
}

/**
 * Build database info string
 */
export function buildDbInfoString({
  host,
  port,
  database,
  user,
  password,
  connectTimeout,
  options,
}: DbConnectionSettings): string {
  const segments: string[] = [];
  if (host) segments.push(`host=${host}`);
  if (port) segments.push(`port=${port}`);
  if (database) segments.push(`dbname=${database}`);
  if (user) segments.push(`user=${user}`);
  if (password) segments.push(`password=${password}`);
  if (connectTimeout) segments.push(`connect_timeout=${connectTimeout}`);
  if (options) {
    const optionTokens = options
      .split(/\s+/)
      .map((token) => token.trim())
      .filter(Boolean);
    segments.push(...optionTokens);
  }
  return segments.join(' ').trim();
}

/**
 * Normalize general settings for comparison
 */
export function normalizeGeneralSettings(data: CdrSettingsData) {
  return {
    dbInfo: buildDbInfoString(data),
    dbTable: data.dbTable.trim(),
    legs: data.legs,
    spoolFormat: data.spoolFormat,
    rotateOnHup: data.rotateOnHup,
    debug: data.debug,
  };
}

